import { SANDBOX_LABEL, planSourceLabel, CREDIT_SOURCE_LABELS } from '../utils/creditSource';
import UserRemarks from './UserRemarks';
import UserCreditReservations from './UserCreditReservations';
import { formatDuration } from '../utils/durationFormat';
import { useState, useEffect, useRef } from 'react';
import { FiX, FiUser, FiUsers, FiDollarSign, FiCreditCard, FiCalendar, FiSmartphone, FiStar, FiShield, FiPlus, FiCheckCircle, FiXCircle, FiClock, FiAlertCircle, FiCamera, FiHeart, FiTool, FiLogIn } from 'react-icons/fi';
import { BsGem } from 'react-icons/bs';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { apiRequest } from '../utils/auth';
import { formatDiamonds } from '../utils/diamondFormat';
import { lockAppScroll } from '../utils/scrollLock';
import { useEscapeToClose } from '../utils/useEscapeToClose';
import { useCursorFeed, type CursorPage } from '../hooks/useCursorFeed';
import LazyListFooter from './LazyListFooter';
import './UserDetailsModal.css';

/** Plan id → display label. Unknown/absent plans read as Free. */
const PLAN_LABELS: Record<string, string> = {
  free: 'Free',
  basic: 'Basic',
  pro: 'Pro',
  premium: 'Premium',
};

/** Mirrors the backend's cap on the per-user referral override — see setUserReferralLimit. */
const MAX_REFERRAL_LIMIT = 1000;


interface User {
  _id: string;
  userId: string;
  email?: string;
  name?: string;
  preferred_username?: string;
  preferredName?: string;
  profilePicture?: string;
  originalProfilePicture?: string;
  plan?: string;
  pendingPlan?: string;
  planSource?: string;
  planAcquisitionSource?: string;
  planPromoCode?: string;
  planEnvironment?: 'sandbox' | 'production';
  planGrantedBy?: string;
  planGrantedReason?: string;
  planChangedAt?: string;
  subscribePlatform?: 'AppleAppStore' | 'GooglePlayStore' | 'WebStore';
  lastPlatform?: 'ios' | 'android';
  lastOsVersion?: string;
  lastDeviceModel?: string;
  lastAppVersion?: string;
  lastActiveAt?: string;
  lastScreenName?: string;
  lastScreenAt?: string;
  planCredits: number;
  topUpCredits: number;
  freeCredits: number;
  totalCredits: number;
  unlockedExtensionsCount: number;
  lastBillingDate?: string;
  nextBillingDate?: string;
  followingCount: number;
  followersCount: number;
  likedExtensionsCount: number;
  bookmarkedExtensionsCount: number;
  isCreator?: boolean;
  isCreatorDisabled?: boolean;
  isAdmin?: boolean;
  isInternal?: boolean;
  canSetExtensionPublic?: boolean;
  referralCode?: string | null;
  referralCount?: number;
  /** Per-user cap override; null/absent means this user is on the default. */
  maxReferrals?: number | null;
  /** What the backend actually enforces for this user (override or default). */
  effectiveMaxReferrals?: number;
  defaultMaxReferrals?: number;
  createdAt?: string;
  updatedAt?: string;
}

interface CreditRecord {
  _id: string;
  type: 'free' | 'topup' | 'monthly' | 'yearly';
  status: 'pending' | 'completed' | 'cancelled';
  creditsAmount: number;
  usedAmount: number;
  remainingAmount: number;
  expiresAt?: string;
  productId?: string;
  /** Top-up product title (store snapshot, or a fallback name). Absent for non-purchase grants. */
  productName?: string;
  /** Subscription plan the grant came from — monthly/yearly records only. */
  planId?: string;
  transactionId?: string;
  source?: string;
  promoCode?: string;
  grantedBy?: string;
  sourceReason?: string;
  referralInfo?: {
    direction: 'from' | 'to';
    userId: string;
    name?: string;
    email?: string;
    preferred_username?: string;
  };
  priceAmount?: number;
  priceCurrency?: string;
  /**
   * Only purchase-backed grants carry a store. The backend sends the explicit
   * `'unknown'` sentinel for the rest (free/promo/referral/monthly grants and
   * legacy records) so an API consumer can't mistake a missing property for
   * "not applicable" — it is not something to render.
   */
  store?: 'ios' | 'android' | 'stripe' | 'unknown';
  environment?: 'sandbox' | 'production';
  createdAt: string;
  updatedAt: string;
}

interface DiamondTransaction {
  _id: string;
  extensionId?: string;
  extensionName?: string;
  diamondsCount: number;
  type: 'commission' | 'cashout' | 'reversal';
  currentStatus: 'pending' | 'valid' | 'expired';
  validAt?: string;
  expiresAt?: string;
  createdAt: string;
  extensionThumbnail?: string;
  payoutStatus?: string;
}

interface StatusHistoryItem {
  status: string;
  time: string;
  reason?: string;
}

interface ActivityItem {
  _id: string;
  type: 'app_launch' | 'login' | 'image_generation' | 'extension_creation' | 'product_activity' | 'app_session' | 'subscription' | 'topup' | 'credit_grant';
  createdAt: string;
  purchaseStatus?: string;
  productId?: string;
  planId?: string;
  priceAmount?: number;
  priceCurrency?: string;
  environment?: string;
  adminName?: string;
  remark?: string;
  // app_launch / login
  event?: string;
  source?: string;
  imageCount?: number;
  durationMs?: number;
  sessionEnded?: boolean;
  sessionRecovered?: boolean;
  result?: string;
  loginMethod?: string;
  appVersion?: string;
  buildNumber?: string;
  platform?: string;
  osVersion?: string;
  deviceModel?: string;
  // image_generation — one row per credit reservation, matching the credit usage
  // records. Rows without creditStatus are generations that never had one.
  reservationId?: string;
  operationType?: 'transform' | 'blend' | 'extension' | 'createExtension' | 'upscale' | 'custom';
  credits?: number;
  creditStatus?: 'reserved' | 'confirmed' | 'released' | 'expired' | 'failed';
  generationId?: string;
  /** deleted: the user removed the image from their gallery; the charge stands. */
  imageState?: 'present' | 'deleted' | 'unlinked';
  /** A later retry of the same image ran under a different reservation. */
  retried?: boolean;
  extensionId?: string;
  extensionName?: string;
  mode?: string;
  status?: 'pending' | 'processing' | 'completed' | 'failed';
  aiModel?: string;
  errorMessage?: string;
  // extension_creation — the lens's per-use price, not a charge
  creditCost?: number;
  isPublished?: boolean;
  reportStatus?: string;
}

const ACTIVITY_CONFIG: Record<ActivityItem['type'], { icon: React.ReactNode; color: string; label: string }> = {
  subscription: { icon: <FiCreditCard />, color: 'var(--color-success)', label: 'Subscription' },
  topup: { icon: <FiDollarSign />, color: 'var(--color-success)', label: 'Top-up' },
  credit_grant: { icon: <FiPlus />, color: 'var(--color-accent)', label: 'Credit / plan grant' },
  app_session: { icon: <FiClock />, color: 'var(--color-accent)', label: 'App session' },
  product_activity: { icon: <FiCamera />, color: 'var(--color-accent)', label: 'Product activity' },
  app_launch: { icon: <FiSmartphone />, color: 'var(--color-accent)', label: 'App launch' },
  login: { icon: <FiLogIn />, color: 'var(--color-warning-text)', label: 'Login' },
  image_generation: { icon: <FiCamera />, color: '#a371f7', label: 'Image generated' },
  extension_creation: { icon: <FiTool />, color: 'var(--color-success)', label: 'Lens created' },
};

const PRODUCT_LABELS: Record<string, string> = {
  download_image: 'Image downloaded', download_image_failed: 'Image save failed', photo_permission: 'Photo permission',
  share_sheet_opened: 'Share opened', share_completed: 'Share completed', share_target_selected: 'Share target selected',
  lens_try: 'Lens selected', lens_draft_saved: 'Lens draft saved', lens_published: 'Lens published',
};

// Same names as the Credit usage page, so a row here can be found there.
const OPERATION_LABELS: Record<NonNullable<ActivityItem['operationType']>, string> = {
  transform: 'Transform',
  blend: 'Blend',
  extension: 'Use lens',
  createExtension: 'Create lens',
  upscale: 'Upscale',
  custom: 'Custom',
};

const CREDIT_STATUS: Record<NonNullable<ActivityItem['creditStatus']>, { label: string; tag: string }> = {
  confirmed: { label: 'charged', tag: 'completed' },
  reserved: { label: 'held', tag: 'pending' },
  released: { label: 'refunded', tag: 'processing' },
  expired: { label: 'refunded · expired', tag: 'processing' },
  failed: { label: 'needs review', tag: 'failed' },
};

type TabType = 'overview' | 'credits' | 'diamonds' | 'creator' | 'ban' | 'activity' | 'admin' | 'remarks';

interface ActivityPage extends CursorPage<ActivityItem> {
  summary?: { downloadedImages: number; sharedImages: number; shareTargetSelections: number };
  presence?: { lastActiveAt?: string; lastScreenName?: string; lastScreenAt?: string };
  usage?: { lastSessionDurationMs: number | null; totalDurationMs: number; sessionCount: number };
}

interface UserDetailsModalProps {
  user: User;
  isOpen: boolean;
  onClose: () => void;
  onStatusChanged?: () => void;
}

const BAN_STATUS_CONFIG: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  active: { label: 'Banned', color: '#e74c3c', icon: <FiXCircle /> },
  inactive: { label: 'Unbanned', color: '#27ae60', icon: <FiCheckCircle /> },
};

const BAN_REASONS = [
  'Sexual or Nudity',
  'Violence or Gore',
  'Hate Speech',
  'Copyright Violation',
  'Spam or Misleading',
  'Multiple Policy Violations',
  'Other Policy Violation',
];

export default function UserDetailsModal({ user, isOpen, onClose, onStatusChanged }: UserDetailsModalProps) {
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [creditHistory, setCreditHistory] = useState<CreditRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [diamondTransactions, setDiamondTransactions] = useState<DiamondTransaction[]>([]);
  const [diamondLoading, setDiamondLoading] = useState(false);
  const [addCreditsAmount, setAddCreditsAmount] = useState('');
  const [addCreditsReason, setAddCreditsReason] = useState('');
  const [addCreditsExpiry, setAddCreditsExpiry] = useState<Date | null>(null);
  const [addCreditsLoading, setAddCreditsLoading] = useState(false);
  const [addCreditsMessage, setAddCreditsMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  // Ban tab state
  const [banHistory, setBanHistory] = useState<StatusHistoryItem[]>([]);
  const [banHistoryLoading, setBanHistoryLoading] = useState(false);
  const [isCreatorDisabled, setIsCreatorDisabled] = useState(user.isCreatorDisabled ?? false);
  const [banReason, setBanReason] = useState('');
  const [unbanReason, setUnbanReason] = useState('');
  const [banActionLoading, setBanActionLoading] = useState(false);
  const [banActionError, setBanActionError] = useState<string | null>(null);

  // Creator tab state
  const [isCreator, setIsCreator] = useState(user.isCreator ?? false);

  // Activity tab state
  const [activityCategory, setActivityCategory] = useState<'all' | 'billing'>('all');
  const activityScrollRef = useRef<HTMLDivElement>(null);
  const activityFeed = useCursorFeed<ActivityItem, ActivityPage>(isOpen && (activeTab === 'activity' || activeTab === 'overview')
    ? `/api/admin/users/${user.userId}/activity?limit=25${activeTab === 'activity' && activityCategory === 'billing' ? '&category=billing' : ''}` : null, '_id', 'type');
  const activityLogs = activityFeed.items;
  const activitySummary = activityFeed.response?.summary;
  const activitySummaryOwner = activityFeed.response ? user.userId : null;
  const activityPresence = activityFeed.response?.presence;
  const activityUsage = activityFeed.response?.usage;

  const [userExtensions, setUserExtensions] = useState<{ _id: string; Name: string; thumbnailImageUrl?: string; CreditCost: number; likeCount: number; isPublished: boolean; reportStatus: string; createdAt: string }[]>([]);
  const [extensionsLoading, setExtensionsLoading] = useState(false);

  // Public extension permission state
  const [canSetExtensionPublic, setCanSetExtensionPublic] = useState(user.canSetExtensionPublic ?? false);
  const [publicExtToggleLoading, setPublicExtToggleLoading] = useState(false);

  // Admin access state
  const [isAdmin, setIsAdmin] = useState(user.isAdmin ?? false);
  const [adminToggleLoading, setAdminToggleLoading] = useState(false);

  // Internal / test account state (excluded from conversion & growth stats)
  const [isInternal, setIsInternal] = useState(user.isInternal ?? false);
  const [internalToggleLoading, setInternalToggleLoading] = useState(false);

  // Referral cap override state. The input is blank when the user is on the
  // default, so "blank" is what clears the override.
  const [referralLimitInput, setReferralLimitInput] = useState(
    typeof user.maxReferrals === 'number' ? String(user.maxReferrals) : ''
  );
  const [referralLimitOverride, setReferralLimitOverride] = useState<number | null>(user.maxReferrals ?? null);
  const [referralLimitSaving, setReferralLimitSaving] = useState(false);
  const [referralLimitMessage, setReferralLimitMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Stats (mutable for credits update)
  const [totalCredits, setTotalCredits] = useState(user.totalCredits);
  const [extensionsCreatedCount, setExtensionsCreatedCount] = useState(0);

  // Relationship sub-modal
  const [relationshipModal, setRelationshipModal] = useState<{ type: 'followers' | 'following' | 'liked' | 'bookmarked'; title: string } | null>(null);
  const [relationshipItems, setRelationshipItems] = useState<any[]>([]);
  const [relationshipLoading, setRelationshipLoading] = useState(false);

  // Lock background scroll while the modal is open
  useEffect(() => (isOpen ? lockAppScroll() : undefined), [isOpen]);

  // Escape dismisses the top layer only — the relationship sub-modal is declared
  // second, so it closes first and leaves this modal standing.
  useEscapeToClose(isOpen, onClose);
  useEscapeToClose(!!relationshipModal, () => setRelationshipModal(null));

  useEffect(() => {
    if (!isOpen) return;
    setIsCreatorDisabled(user.isCreatorDisabled ?? false);
    setIsCreator(user.isCreator ?? false);
    setCanSetExtensionPublic(user.canSetExtensionPublic ?? false);
    setIsAdmin(user.isAdmin ?? false);
    setIsInternal(user.isInternal ?? false);
    setReferralLimitOverride(user.maxReferrals ?? null);
    setReferralLimitInput(typeof user.maxReferrals === 'number' ? String(user.maxReferrals) : '');
    setReferralLimitMessage(null);
    setTotalCredits(user.totalCredits);
    fetchExtensionsCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, user]);

  useEffect(() => {
    if (!isOpen || activeTab !== 'credits') return;
    fetchCreditHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, activeTab, user.userId]);

  useEffect(() => {
    if (!isOpen || activeTab !== 'diamonds') return;
    fetchDiamondTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, activeTab, user.userId]);

  useEffect(() => {
    if (!isOpen || activeTab !== 'ban') return;
    fetchBanHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, activeTab, user.userId]);

  useEffect(() => {
    if (!isOpen || activeTab !== 'creator') return;
    fetchUserExtensions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, activeTab, user.userId]);

  useEffect(() => {
    if (activityScrollRef.current) activityScrollRef.current.scrollTop = 0;
  }, [isOpen, activeTab, user.userId, activityCategory]);

  const fetchCreditHistory = async () => {
    try {
      setLoading(true);
      const res = await apiRequest<{ success: boolean; data: CreditRecord[]; pagination: unknown }>(`/api/admin/users/${user.userId}/credit-history?limit=100`);
      if (res.success && res.data) setCreditHistory(res.data);
    } catch (err) {
      console.error('Failed to load credit history', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchDiamondTransactions = async () => {
    try {
      setDiamondLoading(true);
      const res = await apiRequest<{ success: boolean; data: DiamondTransaction[]; pagination: unknown }>(`/api/admin/users/${user.userId}/diamond-transactions?limit=100`);
      if (res.success && res.data) setDiamondTransactions(res.data);
    } catch (err) {
      console.error('Failed to load diamond transactions', err);
    } finally {
      setDiamondLoading(false);
    }
  };

  const fetchBanHistory = async () => {
    try {
      setBanHistoryLoading(true);
      const res = await apiRequest<{
        success: boolean;
        userId: string;
        isCreator: boolean;
        isCreatorDisabled: boolean;
        statusHistory: StatusHistoryItem[];
      }>(`/api/admin/users/${user.userId}/ban-history`);
      if (res.success) {
        setBanHistory(res.statusHistory || []);
        setIsCreatorDisabled(res.isCreatorDisabled);
      }
    } catch (err) {
      console.error('Failed to load ban history', err);
    } finally {
      setBanHistoryLoading(false);
    }
  };

  const handleAddCredits = async () => {
    const amount = parseInt(addCreditsAmount);
    if (!amount || amount <= 0) return;
    setAddCreditsLoading(true);
    setAddCreditsMessage(null);
    // Send the picked date as a local YYYY-MM-DD string; the backend treats it
    // as end of that day (23:59:59.999). Omit entirely when no date is picked.
    const expiresAt = addCreditsExpiry
      ? `${addCreditsExpiry.getFullYear()}-${String(addCreditsExpiry.getMonth() + 1).padStart(2, '0')}-${String(addCreditsExpiry.getDate()).padStart(2, '0')}`
      : null;
    try {
      await apiRequest(`/api/admin/users/${user.userId}/add-credits`, {
        method: 'POST',
        body: JSON.stringify({
          amount,
          reason: addCreditsReason || 'Admin credit',
          ...(expiresAt ? { expiresAt } : {}),
        }),
      });
      setAddCreditsMessage({ type: 'success', text: `Added ${amount} credits successfully` });
      setAddCreditsAmount('');
      setAddCreditsReason('');
      setAddCreditsExpiry(null);
      setTotalCredits(prev => prev + amount);
      fetchCreditHistory();
    } catch (err) {
      setAddCreditsMessage({ type: 'error', text: err instanceof Error ? err.message : 'Failed to add credits' });
    } finally {
      setAddCreditsLoading(false);
    }
  };

  const handleRevokeCredit = async (record: CreditRecord) => {
    const reason = window.prompt(
      `Revoke ${record.creditsAmount} ${record.type} credit(s) — remaining ${record.remainingAmount}?\n\n` +
        `This cancels the record and removes its remaining balance from the user. ` +
        `Already-spent credits are not clawed back.\n\nOptional reason:`,
      '',
    );
    if (reason === null) return; // admin cancelled the prompt
    setRevokingId(record._id);
    try {
      await apiRequest(`/api/admin/users/${user.userId}/credit-records/${record._id}/revoke`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      });
      await fetchCreditHistory();
      onStatusChanged?.();
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Failed to revoke credit record');
    } finally {
      setRevokingId(null);
    }
  };

  const handleBan = async () => {
    if (!banReason) return;
    setBanActionLoading(true);
    setBanActionError(null);
    try {
      await apiRequest(`/api/admin/users/${user.userId}/ban-creator`, {
        method: 'POST',
        body: JSON.stringify({ reason: banReason }),
      });
      setBanReason('');
      setIsCreatorDisabled(true);
      await fetchBanHistory();
      onStatusChanged?.();
    } catch (err) {
      setBanActionError(err instanceof Error ? err.message : 'Failed to ban creator');
    } finally {
      setBanActionLoading(false);
    }
  };

  const handleUnban = async () => {
    setBanActionLoading(true);
    setBanActionError(null);
    try {
      await apiRequest(`/api/admin/users/${user.userId}/unban-creator`, {
        method: 'POST',
        body: JSON.stringify({ reason: unbanReason || 'Unbanned by admin' }),
      });
      setUnbanReason('');
      setIsCreatorDisabled(false);
      await fetchBanHistory();
      onStatusChanged?.();
    } catch (err) {
      setBanActionError(err instanceof Error ? err.message : 'Failed to unban creator');
    } finally {
      setBanActionLoading(false);
    }
  };

  const fetchUserExtensions = async () => {
    try {
      setExtensionsLoading(true);
      const res = await apiRequest<{ success: boolean; extensions: typeof userExtensions }>(`/api/admin/users/${user.userId}/extensions`);
      if (res.success && res.extensions) setUserExtensions(res.extensions);
    } catch (err) {
      console.error('Failed to load user extensions', err);
    } finally {
      setExtensionsLoading(false);
    }
  };


  const handleTogglePublicExtension = async () => {
    const newValue = !canSetExtensionPublic;
    if (!newValue) {
      const confirmed = window.confirm(
        'Revoking public lens permission will set all of this user\'s public lenses to private. Continue?'
      );
      if (!confirmed) return;
    }
    setPublicExtToggleLoading(true);
    try {
      await apiRequest(`/api/admin/users/${user.userId}/toggle-public-extension`, {
        method: 'POST',
        body: JSON.stringify({ canSetExtensionPublic: newValue }),
      });
      setCanSetExtensionPublic(newValue);
      onStatusChanged?.();
      if (newValue === false) fetchUserExtensions();
    } catch (err) {
      console.error('Failed to toggle public extension permission', err);
    } finally {
      setPublicExtToggleLoading(false);
    }
  };

  const handleToggleAdmin = async () => {
    const newValue = !isAdmin;
    const confirmed = window.confirm(
      newValue
        ? 'Grant this user Admin Portal access? They will be able to manage all users and content.'
        : 'Revoke this user\'s Admin Portal access?'
    );
    if (!confirmed) return;
    setAdminToggleLoading(true);
    try {
      await apiRequest(`/api/admin/users/${user.userId}/toggle-admin`, {
        method: 'POST',
        body: JSON.stringify({ isAdmin: newValue }),
      });
      setIsAdmin(newValue);
      onStatusChanged?.();
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Failed to update admin access');
    } finally {
      setAdminToggleLoading(false);
    }
  };

  const handleToggleInternal = async () => {
    const newValue = !isInternal;
    setInternalToggleLoading(true);
    try {
      await apiRequest(`/api/admin/users/${user.userId}/toggle-internal`, {
        method: 'POST',
        body: JSON.stringify({ isInternal: newValue }),
      });
      setIsInternal(newValue);
      onStatusChanged?.();
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Failed to update internal flag');
    } finally {
      setInternalToggleLoading(false);
    }
  };

  /**
   * Saves the referral cap override. A blank input clears it (sends null), which
   * puts the user back on the programme default rather than capping them at 0.
   */
  const handleSaveReferralLimit = async () => {
    const trimmed = referralLimitInput.trim();
    const parsed = trimmed === '' ? null : Number(trimmed);

    if (parsed !== null && (!Number.isInteger(parsed) || parsed < 0 || parsed > MAX_REFERRAL_LIMIT)) {
      setReferralLimitMessage({
        type: 'error',
        text: `Enter a whole number between 0 and ${MAX_REFERRAL_LIMIT}, or leave it blank for the default.`,
      });
      return;
    }

    setReferralLimitSaving(true);
    setReferralLimitMessage(null);
    try {
      const res = await apiRequest<{ success: boolean; maxReferrals: number | null; effectiveMaxReferrals: number }>(
        `/api/admin/users/${user.userId}/referral-limit`,
        { method: 'POST', body: JSON.stringify({ maxReferrals: parsed }) }
      );
      setReferralLimitOverride(res.maxReferrals ?? null);
      setReferralLimitInput(typeof res.maxReferrals === 'number' ? String(res.maxReferrals) : '');
      setReferralLimitMessage({
        type: 'success',
        text: parsed === null
          ? 'Reset to the programme default.'
          : `This user's code can now be redeemed ${parsed} ${parsed === 1 ? 'time' : 'times'}.`,
      });
      onStatusChanged?.();
    } catch (err) {
      setReferralLimitMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Failed to update referral limit',
      });
    } finally {
      setReferralLimitSaving(false);
    }
  };

  const fetchExtensionsCount = async () => {
    try {
      const res = await apiRequest<{ success: boolean; extensions: any[] }>(`/api/admin/users/${user.userId}/extensions`);
      if (res.success && res.extensions) setExtensionsCreatedCount(res.extensions.length);
    } catch { /* ignore */ }
  };

  const openRelationshipModal = async (type: 'followers' | 'following' | 'liked' | 'bookmarked') => {
    const titles: Record<string, string> = { followers: 'Followers', following: 'Following', liked: 'Liked Lenses', bookmarked: 'Bookmarked Lenses' };
    setRelationshipModal({ type, title: titles[type] });
    setRelationshipItems([]);
    setRelationshipLoading(true);
    try {
      const res = await apiRequest<{ success: boolean; items: any[] }>(`/api/admin/users/${user.userId}/relationships?type=${type}`);
      if (res.success && res.items) setRelationshipItems(res.items);
    } catch (err) {
      console.error('Failed to load relationships', err);
    } finally {
      setRelationshipLoading(false);
    }
  };

  const currentPresence = activitySummaryOwner === user.userId && activityPresence ? activityPresence : user;

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  const formatShortDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatCurrency = (amount?: number, currency?: string) => {
    if (amount == null) return '-';
    return `${currency?.toUpperCase() || 'USD'} ${amount.toFixed(2)}`;
  };

  const getPlanConfig = (plan?: string) => {
    switch (plan) {
      case 'basic': return { label: 'Basic', color: 'var(--color-accent)', bg: '#1a2744', border: 'var(--color-accent-strong)' };
      case 'pro': return { label: 'Pro', color: '#56d364', bg: '#1a2e1f', border: 'var(--color-success)' };
      case 'premium': return { label: 'Premium', color: '#a371f7', bg: '#2d1f4e', border: '#8957e5' };
      default: return { label: 'Free', color: 'var(--color-muted)', bg: 'var(--color-surface-hover)', border: 'var(--color-border)' };
    }
  };

  const renderPlanTag = (plan: string, className: string) => {
    const config = getPlanConfig(plan);
    return (
      <span className={className} style={{ color: config.color, background: config.bg, borderColor: config.border }}>
        {config.label}
      </span>
    );
  };

  const getInitials = () => {
    const n = user.preferredName || user.name || user.email || '?';
    return n.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
  };

  if (!isOpen) return null;

  const planConfig = getPlanConfig(user.plan);
  // Referral cap: the backend sends the programme default alongside the override
  // so the portal never has to keep its own copy of the number.
  const defaultMaxReferrals = user.defaultMaxReferrals ?? null;
  const effectiveMaxReferrals = referralLimitOverride ?? defaultMaxReferrals;
  const referralCount = user.referralCount ?? 0;
  const banStatusBadge = isCreatorDisabled
    ? { color: '#e74c3c', label: 'Banned' }
    : { color: '#27ae60', label: 'Active' };

  return (
    <div className="um-overlay" onClick={onClose}>
      <div className="um-content" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="um-header">
          <div className="um-profile">
            {user.profilePicture ? (
              <img src={user.profilePicture} alt="" className="um-avatar" />
            ) : (
              <div className="um-avatar-placeholder">{getInitials()}</div>
            )}
            <div className="um-profile-info">
              <h2 className="um-name">{user.preferredName || user.name || 'Unknown User'}</h2>
              <div className="um-meta">
                {user.email && <span className="um-email">{user.email}</span>}
                {user.preferred_username && <span className="um-username">@{user.preferred_username}</span>}
              </div>
              <div className="um-badges">
                <span className="um-plan-badge" style={{ color: planConfig.color, background: planConfig.bg, borderColor: planConfig.border }}>
                  {planConfig.label}
                </span>
                {user.pendingPlan && <span className="um-pending-badge">→ {getPlanConfig(user.pendingPlan).label}</span>}
                {user.isCreator && (
                  <span className={`um-creator-badge ${user.isCreatorDisabled ? 'banned' : ''}`}>
                    <FiStar /> {user.isCreatorDisabled ? 'Creator (Banned)' : 'Creator'}
                  </span>
                )}
                {isAdmin && (
                  <span className="um-creator-badge" style={{ color: 'var(--color-warning)', borderColor: 'var(--color-warning)', background: '#3a2a17' }}>
                    <FiShield /> Admin
                  </span>
                )}
                {isInternal && (
                  <span className="um-creator-badge" style={{ color: 'var(--color-muted)', borderColor: 'var(--color-muted)', background: 'var(--color-surface-hover)' }}>
                    <FiTool /> Internal
                  </span>
                )}
              </div>
            </div>
          </div>
          <button className="um-close" onClick={onClose}><FiX /></button>
        </div>

        {/* Stats Row */}
        <div className="um-stats-row">
          <div className="um-stat"><span className="um-stat-value">{totalCredits}</span><span className="um-stat-label">Credits</span></div>
          <div className="um-stat-divider" />
          <div className="um-stat um-stat-clickable" onClick={() => openRelationshipModal('followers')}><span className="um-stat-value">{user.followersCount}</span><span className="um-stat-label">Followers</span></div>
          <div className="um-stat-divider" />
          <div className="um-stat um-stat-clickable" onClick={() => openRelationshipModal('following')}><span className="um-stat-value">{user.followingCount}</span><span className="um-stat-label">Following</span></div>
          <div className="um-stat-divider" />
          <div className="um-stat um-stat-clickable" onClick={() => openRelationshipModal('liked')}><span className="um-stat-value">{user.likedExtensionsCount}</span><span className="um-stat-label">Liked</span></div>
          <div className="um-stat-divider" />
          <div className="um-stat um-stat-clickable" onClick={() => openRelationshipModal('bookmarked')}><span className="um-stat-value">{user.bookmarkedExtensionsCount}</span><span className="um-stat-label">Bookmarked</span></div>
          <div className="um-stat-divider" />
          <div className="um-stat"><span className="um-stat-value">{extensionsCreatedCount}</span><span className="um-stat-label">Lenses</span></div>
        </div>

        {/* Tabs */}
        <div className="um-tabs">
          <button className={`um-tab ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => setActiveTab('overview')}>
            <FiUser /> Overview
          </button>
          <button className={`um-tab ${activeTab === 'credits' ? 'active' : ''}`} onClick={() => setActiveTab('credits')}>
            <FiDollarSign /> Credits & Billing
          </button>
          <button className={`um-tab ${activeTab === 'diamonds' ? 'active' : ''}`} onClick={() => setActiveTab('diamonds')}>
            <BsGem /> Diamonds
          </button>
          <button className={`um-tab ${activeTab === 'creator' ? 'active' : ''}`} onClick={() => setActiveTab('creator')}>
            <FiStar /> Creator
          </button>
          <button className={`um-tab ${activeTab === 'activity' ? 'active' : ''}`} onClick={() => setActiveTab('activity')}>
            <FiSmartphone /> Activity
          </button>
          <button className={`um-tab ${activeTab === 'remarks' ? 'active' : ''}`} onClick={() => setActiveTab('remarks')}>
            <FiClock /> Remarks
          </button>
          <button className={`um-tab ${activeTab === 'admin' ? 'active' : ''}`} onClick={() => setActiveTab('admin')}>
            <FiShield /> Admin
          </button>
          {isCreator && (
            <button className={`um-tab ${activeTab === 'ban' ? 'active' : ''}`} onClick={() => setActiveTab('ban')}>
              <FiShield /> Creator Ban
              {isCreatorDisabled && <span className="um-tab-badge">!</span>}
            </button>
          )}
        </div>

        <div className={`um-body${activeTab === 'remarks' ? ' um-body-remarks' : activeTab === 'activity' ? ' um-body-activity' : ''}`}>
          {/* ===== OVERVIEW TAB ===== */}
          {activeTab === 'overview' && (
            <div className="um-overview">
              <div className="um-card">
                <div className="um-card-header"><FiCreditCard className="um-card-icon" /><h3>Credits Breakdown</h3></div>
                <div className="um-card-body">
                  <div className="um-row"><span>Plan Credits</span><span className="um-val">{user.planCredits}</span></div>
                  <div className="um-row"><span>Top-up Credits</span><span className="um-val">{user.topUpCredits}</span></div>
                  <div className="um-row"><span>Free Credits</span><span className="um-val">{user.freeCredits}</span></div>
                  <div className="um-row um-row-total"><span>Total</span><span className="um-val">{user.totalCredits}</span></div>
                </div>
              </div>

              <div className="um-card">
                <div className="um-card-header"><FiCalendar className="um-card-icon" /><h3>Subscription</h3></div>
                <div className="um-card-body">
                  <div className="um-row">
                    <span>Current Plan</span>
                    <span className="um-plan-tag" style={{ color: planConfig.color, background: planConfig.bg, borderColor: planConfig.border }}>{planConfig.label}</span>
                  </div>
                  <div className="um-row"><span>Subscription source</span><span className="um-val">{planSourceLabel(user.plan, user.planSource, user.planAcquisitionSource)}</span></div>
                  {user.planEnvironment === 'sandbox' && <div className="um-row"><span>Environment</span><span className="um-source-tag sandbox">{SANDBOX_LABEL}</span></div>}
                  {user.planPromoCode && <div className="um-row"><span>Promo code</span><span className="um-val">{user.planPromoCode}</span></div>}
                  {user.planSource === 'admin' && user.planGrantedBy && <div className="um-row"><span>Granted by</span><span className="um-val">{user.planGrantedBy}</span></div>}
                  {user.planSource === 'admin' && user.planGrantedReason && <div className="um-row"><span>Grant reason</span><span className="um-val um-remark-text">{user.planGrantedReason}</span></div>}
                  {user.pendingPlan && (
                    <div className="um-row"><span>Pending Plan</span><span className="um-val um-pending">{getPlanConfig(user.pendingPlan).label}</span></div>
                  )}
                  <div className="um-row">
                    <span>Platform</span>
                    {user.subscribePlatform ? (
                      <span className={`um-platform ${user.subscribePlatform}`}>
                        <FiSmartphone /> {user.subscribePlatform === 'AppleAppStore' ? 'iOS' : user.subscribePlatform === 'GooglePlayStore' ? 'Android' : 'Web'}
                      </span>
                    ) : <span className="um-val">-</span>}
                  </div>
                  <div className="um-row"><span>Last Billing</span><span className="um-val">{formatShortDate(user.lastBillingDate)}</span></div>
                  <div className="um-row"><span>Next Billing</span><span className="um-val">{formatShortDate(user.nextBillingDate)}</span></div>
                </div>
              </div>

              <div className="um-card um-card-full">
                <div className="um-card-header"><FiUser className="um-card-icon" /><h3>Account Details</h3></div>
                <div className="um-account-grid">
                  <div className="um-account-item"><span className="um-account-label">User ID</span><span className="um-account-value mono">{user.userId}</span></div>
                  <div className="um-account-item"><span className="um-account-label">Email</span><span className="um-account-value">{user.email || '-'}</span></div>
                  <div className="um-account-item"><span className="um-account-label">Display Name</span><span className="um-account-value">{user.preferredName || user.name || '-'}</span></div>
                  <div className="um-account-item"><span className="um-account-label">Username</span><span className="um-account-value">{user.preferred_username || '-'}</span></div>
                  <div className="um-account-item">
                    <span className="um-account-label">Device Platform</span>
                    <span className="um-account-value">
                      {user.lastPlatform
                        ? `${user.lastPlatform === 'ios' ? 'iOS' : 'Android'}${user.lastOsVersion ? ` ${user.lastOsVersion}` : ''}`
                        : '-'}
                    </span>
                  </div>
                  <div className="um-account-item"><span className="um-account-label">Last Session Duration</span><span className="um-account-value">{formatDuration(activitySummaryOwner === user.userId ? activityUsage?.lastSessionDurationMs : null)}</span></div>
                  <div className="um-account-item"><span className="um-account-label">Total Duration</span><span className="um-account-value">{formatDuration(activitySummaryOwner === user.userId ? activityUsage?.totalDurationMs : null)}</span></div>
                  <div className="um-account-item"><span className="um-account-label">Last Page</span><span className="um-account-value">{currentPresence.lastScreenName?.replace(/_/g, ' ') || '-'}</span></div>
                  <div className="um-account-item"><span className="um-account-label">Last Page Viewed</span><span className="um-account-value">{formatDate(currentPresence.lastScreenAt)}</span></div>
                  <div className="um-account-item"><span className="um-account-label">Downloaded Images</span><span className="um-account-value">{activitySummaryOwner === user.userId ? activitySummary?.downloadedImages ?? '-' : '-'}</span></div>
                  <div className="um-account-item"><span className="um-account-label">Shared Images (confirmed)</span><span className="um-account-value">{activitySummaryOwner === user.userId ? activitySummary?.sharedImages ?? '-' : '-'}</span></div>
                  <div className="um-account-item"><span className="um-account-label">Share Targets Selected (Android)</span><span className="um-account-value">{activitySummaryOwner === user.userId ? activitySummary?.shareTargetSelections ?? '-' : '-'}</span></div>
                  <div className="um-account-item"><span className="um-account-label">Joined</span><span className="um-account-value">{formatShortDate(user.createdAt)}</span></div>
                  <div className="um-account-item"><span className="um-account-label">Last Updated</span><span className="um-account-value">{formatShortDate(user.updatedAt)}</span></div>
                </div>
              </div>
            </div>
          )}

          {/* ===== ADMIN TAB ===== */}
          {activeTab === 'admin' && (
            <div className="um-overview">
              <div className="um-card um-card-full">
                <div className="um-card-header"><FiShield className="um-card-icon" /><h3>Admin Access</h3></div>
                <div className="um-card-body">
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                      <div style={{ fontWeight: 500, color: 'var(--color-text)', marginBottom: '4px' }}>
                        Admin Portal Access
                      </div>
                      <div style={{ fontSize: '13px', color: 'var(--color-muted)' }}>
                        {isAdmin
                          ? 'This user is an admin and can access the Admin Portal.'
                          : 'Assign this user as an admin to grant Admin Portal access.'}
                      </div>
                    </div>
                    <button
                      onClick={handleToggleAdmin}
                      disabled={adminToggleLoading}
                      style={{
                        padding: '8px 20px',
                        borderRadius: '6px',
                        border: 'none',
                        cursor: adminToggleLoading ? 'not-allowed' : 'pointer',
                        fontWeight: 500,
                        fontSize: '14px',
                        backgroundColor: isAdmin ? '#e74c3c' : 'var(--color-success-strong)',
                        color: 'white',
                        opacity: adminToggleLoading ? 0.6 : 1,
                      }}
                    >
                      {adminToggleLoading ? 'Updating...' : isAdmin ? 'Revoke Admin' : 'Make Admin'}
                    </button>
                  </div>
                </div>
              </div>

              <div className="um-card um-card-full">
                <div className="um-card-header"><FiTool className="um-card-icon" /><h3>Internal / Test Account</h3></div>
                <div className="um-card-body">
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                      <div style={{ fontWeight: 500, color: 'var(--color-text)', marginBottom: '4px' }}>
                        Internal Account
                      </div>
                      <div style={{ fontSize: '13px', color: 'var(--color-muted)' }}>
                        {isInternal
                          ? 'Flagged as internal/test — excluded from conversion & growth statistics.'
                          : 'Mark as an internal/test account to exclude it from conversion & growth statistics.'}
                      </div>
                    </div>
                    <button
                      onClick={handleToggleInternal}
                      disabled={internalToggleLoading}
                      style={{
                        padding: '8px 20px',
                        borderRadius: '6px',
                        border: 'none',
                        cursor: internalToggleLoading ? 'not-allowed' : 'pointer',
                        fontWeight: 500,
                        fontSize: '14px',
                        backgroundColor: isInternal ? '#e74c3c' : 'var(--color-success-strong)',
                        color: 'white',
                        opacity: internalToggleLoading ? 0.6 : 1,
                      }}
                    >
                      {internalToggleLoading ? 'Updating...' : isInternal ? 'Unmark Internal' : 'Mark Internal'}
                    </button>
                  </div>
                </div>
              </div>

              <div className="um-card um-card-full">
                <div className="um-card-header"><FiUsers className="um-card-icon" /><h3>Referral Limit</h3></div>
                <div className="um-card-body">
                  <div className="um-row">
                    <span>Referral Code</span>
                    <span className="um-val mono">{user.referralCode || '-'}</span>
                  </div>
                  <div className="um-row">
                    <span>Referrals Used</span>
                    <span className="um-val">
                      {referralCount}{effectiveMaxReferrals !== null ? ` / ${effectiveMaxReferrals}` : ''}
                    </span>
                  </div>
                  <div className="um-row">
                    <span>Current Limit</span>
                    <span className="um-val">
                      {referralLimitOverride !== null
                        ? `${referralLimitOverride} (custom)`
                        : defaultMaxReferrals !== null ? `${defaultMaxReferrals} (default)` : 'Default'}
                    </span>
                  </div>

                  <div style={{ marginTop: '14px' }}>
                    <div style={{ fontSize: '13px', color: 'var(--color-muted)', marginBottom: '8px' }}>
                      Set how many times this user's referral code can be redeemed. Leave blank to
                      use the default{defaultMaxReferrals !== null ? ` (${defaultMaxReferrals})` : ''}.
                      Lowering it below the used count never revokes existing referrals — the code
                      simply stops accepting new ones.
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <input
                        type="number"
                        className="um-input um-input-sm"
                        placeholder={defaultMaxReferrals !== null ? `Default (${defaultMaxReferrals})` : 'Default'}
                        value={referralLimitInput}
                        onChange={(e) => { setReferralLimitInput(e.target.value); setReferralLimitMessage(null); }}
                        min="0"
                        max={MAX_REFERRAL_LIMIT}
                      />
                      <button
                        className="um-btn-add"
                        onClick={handleSaveReferralLimit}
                        disabled={referralLimitSaving}
                      >
                        {referralLimitSaving ? 'Saving...' : 'Save Limit'}
                      </button>
                      {referralLimitOverride !== null && (
                        <button
                          className="um-btn-add"
                          onClick={() => { setReferralLimitInput(''); setReferralLimitMessage(null); }}
                          disabled={referralLimitSaving}
                          style={{ backgroundColor: 'transparent', border: '1px solid var(--color-border)', color: 'var(--color-muted)' }}
                        >
                          Clear
                        </button>
                      )}
                    </div>
                    {referralLimitMessage && (
                      <div
                        style={{
                          marginTop: '8px',
                          fontSize: '13px',
                          color: referralLimitMessage.type === 'success' ? 'var(--color-success)' : '#e74c3c',
                        }}
                      >
                        {referralLimitMessage.text}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ===== CREDITS & BILLING TAB ===== */}
          {activeTab === 'credits' && (
            <div className="um-credits-tab">
              <UserCreditReservations key={user.userId} userId={user.userId} onReleased={balance => { setTotalCredits(balance); void fetchCreditHistory(); onStatusChanged?.(); }} />
              <div className="um-card">
                <div className="um-card-header"><FiPlus className="um-card-icon" /><h3>Add Credits</h3></div>
                <div className="um-card-body">
                  <div className="um-add-credits">
                    <input type="number" className="um-input um-input-sm" placeholder="Amount" value={addCreditsAmount} onChange={(e) => setAddCreditsAmount(e.target.value)} min="1" />
                    <input type="text" className="um-input um-input-grow" placeholder="Reason (optional)" value={addCreditsReason} onChange={(e) => setAddCreditsReason(e.target.value)} />
                    <div className="um-input-date">
                      <DatePicker
                        selected={addCreditsExpiry}
                        onChange={(date: Date | null) => setAddCreditsExpiry(date)}
                        dateFormat="yyyy-MM-dd"
                        placeholderText="Expiry date (optional)"
                        className="datepicker-input"
                        minDate={new Date()}
                        isClearable
                      />
                    </div>
                    <button className="um-btn-add" onClick={handleAddCredits} disabled={addCreditsLoading || !addCreditsAmount || parseInt(addCreditsAmount) <= 0}>
                      {addCreditsLoading ? 'Adding...' : 'Add Credits'}
                    </button>
                  </div>
                  <div className="um-add-credits-hint">Expiry date is optional — leave blank to default to end of next year.</div>
                  {addCreditsMessage && <div className={`um-msg ${addCreditsMessage.type}`}>{addCreditsMessage.text}</div>}
                </div>
              </div>

              <div className="um-card">
                <div className="um-card-header"><FiCreditCard className="um-card-icon" /><h3>Credit History</h3></div>
                {loading ? (
                  <div className="um-empty">Loading credit history...</div>
                ) : creditHistory.length === 0 ? (
                  <div className="um-empty">No credit records found</div>
                ) : (
                  <div className="um-table-wrap">
                    <table className="um-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Type</th>
                          <th>Source</th>
                          <th>Status</th>
                          <th>Amount</th>
                          <th>Used</th>
                          <th>Remaining</th>
                          <th>Details</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {creditHistory.map((record) => (
                          <tr key={record._id}>
                            <td className="um-td-date">{formatShortDate(record.createdAt)}</td>
                            <td><span className={`um-type-tag ${record.type}`}>{record.type}</span></td>
                            <td><span className="um-source-tag">{CREDIT_SOURCE_LABELS[record.source || 'unknown'] || 'Unknown source'}</span>{record.environment === 'sandbox' && <span className="um-source-tag sandbox">{SANDBOX_LABEL}</span>}</td>
                            <td><span className={`um-status-tag ${record.status}`}>{record.status}</span></td>
                            <td className="um-td-num">{record.creditsAmount}</td>
                            <td className="um-td-num">{record.usedAmount}</td>
                            <td className="um-td-num">{record.remainingAmount}</td>
                            <td>
                              <div className="um-td-details">
                                {record.planId && renderPlanTag(record.planId, 'um-detail-plan')}
                                {record.promoCode && <span>Promo code: {record.promoCode}</span>}
                                {record.grantedBy && <span>Granted by: {record.grantedBy}</span>}
                                {record.sourceReason && <span className="um-remark-text">{record.sourceReason}</span>}
                                {record.productName && <span className="um-product-name">{record.productName}</span>}
                                {record.store && record.store !== 'unknown' && <span className="um-store">{record.store}</span>}
                                {record.priceAmount != null && <span>{formatCurrency(record.priceAmount, record.priceCurrency)}</span>}
                                {record.referralInfo ? (
                                  <span className="um-referral-ref">
                                    {record.referralInfo.direction === 'from' ? 'Referred by: ' : 'Referred: '}
                                    {record.referralInfo.name || record.referralInfo.preferred_username || record.referralInfo.email || record.referralInfo.userId}
                                  </span>
                                ) : (
                                  record.transactionId && <span className="um-txid">{record.transactionId}</span>
                                )}
                                {record.expiresAt && <span>Exp: {formatShortDate(record.expiresAt)}</span>}
                              </div>
                            </td>
                            <td>
                              {record.status !== 'cancelled' && record.remainingAmount > 0 ? (
                                <button
                                  className="um-btn-revoke"
                                  disabled={revokingId === record._id}
                                  onClick={() => handleRevokeCredit(record)}
                                >
                                  {revokingId === record._id ? 'Revoking…' : 'Revoke'}
                                </button>
                              ) : (
                                <span className="um-muted">—</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ===== DIAMONDS TAB ===== */}
          {activeTab === 'diamonds' && (
            <div className="um-credits-tab">
              <div className="um-card">
                <div className="um-card-header"><BsGem className="um-card-icon" /><h3>Diamond Transactions</h3></div>
                {diamondLoading ? (
                  <div className="um-empty">Loading diamond transactions...</div>
                ) : diamondTransactions.length === 0 ? (
                  <div className="um-empty">No diamond transactions found</div>
                ) : (
                  <div className="um-table-wrap">
                    <table className="um-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Lens</th>
                          <th>Type</th>
                          <th>Diamonds</th>
                          <th>Status</th>
                          <th>Expires</th>
                        </tr>
                      </thead>
                      <tbody>
                        {diamondTransactions.map((tx) => {
                          const typeColor = tx.type === 'cashout' ? '#8957e5' : tx.type === 'reversal' ? 'var(--color-subtle)' : 'var(--color-accent-strong)';
                          const statusColor = tx.currentStatus === 'valid' ? 'var(--color-success-strong)' : tx.currentStatus === 'pending' ? 'var(--color-warning-text)' : 'var(--color-subtle)';
                          return (
                            <tr key={tx._id}>
                              <td className="um-td-date">{formatShortDate(tx.createdAt)}</td>
                              <td>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  {tx.extensionThumbnail ? (
                                    <img src={tx.extensionThumbnail} alt="" style={{ width: '28px', height: '28px', borderRadius: '4px', objectFit: 'cover', flexShrink: 0 }} />
                                  ) : (
                                    <div style={{ width: '28px', height: '28px', borderRadius: '4px', backgroundColor: 'var(--color-surface-hover)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-border-strong)', flexShrink: 0 }}><FiCamera size={13} /></div>
                                  )}
                                  <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '160px' }}>
                                    {tx.extensionName || (tx.type === 'cashout' ? 'Cashout' : tx.type === 'reversal' ? 'Reversal' : '-')}
                                  </span>
                                </div>
                              </td>
                              <td><span style={{ padding: '2px 8px', borderRadius: '4px', backgroundColor: typeColor, color: 'white', fontSize: '11px', fontWeight: 600, textTransform: 'capitalize' }}>{tx.type}</span></td>
                              <td className="um-td-num">
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: 'var(--color-accent)', fontWeight: 600, whiteSpace: 'nowrap' }}>
                                  <BsGem size={11} /> {formatDiamonds(tx.diamondsCount)}
                                </span>
                              </td>
                              <td>
                                <span style={{ padding: '2px 8px', borderRadius: '4px', backgroundColor: statusColor, color: 'white', fontSize: '11px', fontWeight: 600, textTransform: 'capitalize' }}>{tx.currentStatus}</span>
                                {tx.type === 'cashout' && tx.payoutStatus && (
                                  <span style={{ marginLeft: '6px', fontSize: '11px', color: 'var(--color-muted)' }}>({tx.payoutStatus})</span>
                                )}
                              </td>
                              <td className="um-td-date">{formatShortDate(tx.expiresAt)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ===== CREATOR TAB ===== */}
          {activeTab === 'creator' && (
            <div className="um-creator-tab">
              {/* Public Extension Permission */}
              <div className="um-card">
                <div className="um-card-header"><FiCheckCircle className="um-card-icon" /><h3>Public Lens Permission</h3></div>
                <div className="um-card-body">
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                      <div style={{ fontWeight: 500, color: 'var(--color-text)', marginBottom: '4px' }}>
                        Can Set Lenses Public
                      </div>
                      <div style={{ fontSize: '13px', color: 'var(--color-muted)' }}>
                        {canSetExtensionPublic
                          ? 'This user can set their lenses to public visibility.'
                          : 'Enable to allow this user to set lenses to public.'}
                      </div>
                      {canSetExtensionPublic && (
                        <div style={{ fontSize: '12px', color: 'var(--color-warning)', marginTop: '4px' }}>
                          ⚠ Revoking will set all public lenses to private.
                        </div>
                      )}
                    </div>
                    <button
                      onClick={handleTogglePublicExtension}
                      disabled={publicExtToggleLoading}
                      style={{
                        padding: '8px 20px',
                        borderRadius: '6px',
                        border: 'none',
                        cursor: publicExtToggleLoading ? 'not-allowed' : 'pointer',
                        fontWeight: 500,
                        fontSize: '14px',
                        backgroundColor: canSetExtensionPublic ? '#e74c3c' : 'var(--color-success-strong)',
                        color: 'white',
                        opacity: publicExtToggleLoading ? 0.6 : 1,
                      }}
                    >
                      {publicExtToggleLoading ? 'Updating...' : canSetExtensionPublic ? 'Revoke Permission' : 'Grant Permission'}
                    </button>
                  </div>
                </div>
              </div>

              {isCreator && (
                <div className="um-card">
                  <div className="um-card-header"><FiCamera className="um-card-icon" /><h3>Extensions ({userExtensions.length})</h3></div>
                  {extensionsLoading ? (
                    <div className="um-empty">Loading lenses...</div>
                  ) : userExtensions.length === 0 ? (
                    <div className="um-empty">No lenses created yet.</div>
                  ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '12px', padding: '16px' }}>
                      {userExtensions.map(ext => (
                        <div key={ext._id} style={{ borderRadius: '8px', overflow: 'hidden', backgroundColor: 'var(--color-surface-hover)', border: '1px solid var(--color-border)' }}>
                          {ext.thumbnailImageUrl ? (
                            <img src={ext.thumbnailImageUrl} alt={ext.Name} style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', display: 'block' }} />
                          ) : (
                            <div style={{ width: '100%', aspectRatio: '1', backgroundColor: 'var(--color-surface-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-border-strong)' }}>
                              <FiCamera size={28} />
                            </div>
                          )}
                          <div style={{ padding: '8px 10px' }}>
                            <div style={{ fontSize: '13px', fontWeight: 500, color: 'var(--color-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {ext.Name}
                              {!ext.isPublished && <span style={{ marginLeft: '6px', fontSize: '10px', padding: '1px 6px', borderRadius: '4px', backgroundColor: '#6c757d', color: 'white' }}>Draft</span>}
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px', fontSize: '12px', color: 'var(--color-muted)' }}>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}><FiHeart size={11} /> {ext.likeCount}</span>
                              <span style={{
                                fontSize: '10px', padding: '1px 6px', borderRadius: '4px',
                                backgroundColor: ext.reportStatus === 'Suspended' ? '#e74c3c' : ext.reportStatus === 'inReview' ? 'var(--color-warning-strong)' : '#27ae60',
                                color: 'white',
                              }}>{ext.reportStatus === 'inReview' ? 'In Review' : ext.reportStatus}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ===== CREATOR BAN TAB ===== */}
          {activeTab === 'ban' && (
            <div className="um-ban-tab">
              {/* Current status bar */}
              <div className="um-ban-status-bar">
                <div className="um-ban-current">
                  <span className="um-ban-status-dot" style={{ background: banStatusBadge.color }} />
                  <span className="um-ban-status-label">
                    Creator Status: <strong style={{ color: banStatusBadge.color }}>{banStatusBadge.label}</strong>
                  </span>
                </div>
              </div>

              {/* Action error */}
              {banActionError && <div className="um-ban-action-error"><FiAlertCircle /> {banActionError}</div>}

              {/* Ban action card — shown when NOT banned */}
              {!isCreatorDisabled && (
                <div className="um-ban-action-card ban-action">
                  <h3><FiAlertCircle /> Ban Creator</h3>
                  <p className="um-ban-action-desc">
                    Banning will disable creator mode and hide all their lenses from users.
                  </p>
                  <select
                    className="um-ban-select"
                    value={banReason}
                    onChange={(e) => setBanReason(e.target.value)}
                  >
                    <option value="">Select a reason...</option>
                    {BAN_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                  <button
                    className="um-ban-action-btn danger"
                    onClick={handleBan}
                    disabled={banActionLoading || !banReason}
                  >
                    {banActionLoading ? 'Banning...' : 'Ban Creator'}
                  </button>
                </div>
              )}

              {/* Unban action card — shown when banned */}
              {isCreatorDisabled && (
                <div className="um-ban-action-card unban-action">
                  <h3><FiCheckCircle /> Unban Creator</h3>
                  <p className="um-ban-action-desc">
                    This will restore creator mode and make their lenses visible again.
                  </p>
                  <textarea
                    className="um-ban-textarea"
                    value={unbanReason}
                    onChange={(e) => setUnbanReason(e.target.value)}
                    rows={2}
                    placeholder="Optional reason..."
                  />
                  <button
                    className="um-ban-action-btn success"
                    onClick={handleUnban}
                    disabled={banActionLoading}
                  >
                    {banActionLoading ? 'Processing...' : 'Unban Creator'}
                  </button>
                </div>
              )}

              {/* Vertical Timeline */}
              <div className="um-ban-timeline-section">
                <h3 className="um-ban-timeline-title">Ban History</h3>
                {banHistoryLoading ? (
                  <div className="um-empty">Loading history...</div>
                ) : banHistory.length === 0 ? (
                  <div className="um-empty">No ban history for this creator.</div>
                ) : (
                  <div className="um-ban-timeline">
                    {[...banHistory].reverse().map((item, idx) => {
                      const config = BAN_STATUS_CONFIG[item.status] || { label: item.status, color: 'var(--color-subtle)', icon: <FiClock /> };
                      return (
                        <div key={idx} className="um-ban-timeline-item">
                          <div className="um-ban-timeline-line-container">
                            <div className="um-ban-timeline-dot" style={{ background: config.color, boxShadow: `0 0 8px color-mix(in srgb, ${config.color} 25%, transparent)` }}>
                              {config.icon}
                            </div>
                            {idx < banHistory.length - 1 && <div className="um-ban-timeline-line" />}
                          </div>
                          <div className="um-ban-timeline-content">
                            <div className="um-ban-timeline-header">
                              <span className="um-ban-timeline-status" style={{ color: config.color }}>{config.label}</span>
                              <span className="um-ban-timeline-time">{formatDate(item.time)}</span>
                            </div>
                            {item.reason && (
                              <p className="um-ban-timeline-reason">{item.reason}</p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'remarks' && <UserRemarks key={user.userId} userId={user.userId} />}

          {/* ===== ACTIVITY TAB ===== */}
          {activeTab === 'activity' && (
            <div className="um-credits-tab um-activity-pane">
              <div className="um-card um-activity-card">
                <div className="um-card-header"><FiClock className="um-card-icon" /><h3>Activity Feed</h3>
                  <select className="um-activity-filter" aria-label="Activity type" value={activityCategory} onChange={event => setActivityCategory(event.target.value as 'all' | 'billing')}>
                    <option value="all">All activity</option>
                    <option value="billing">Subscriptions &amp; credits</option>
                  </select>
                </div>
                <div className="um-activity-scroll" ref={activityScrollRef} aria-busy={activityFeed.loading}>
                {!activityFeed.loading && !activityFeed.error && activityLogs.length === 0 ? (
                  <div className="um-empty">No activity found</div>
                ) : (
                  <div className="um-activity-feed">
                    {activityLogs.map((item) => {
                      const cfg = ACTIVITY_CONFIG[item.type] || ACTIVITY_CONFIG.app_launch;
                      const purchaseLabel = item.purchaseStatus && ({ subscription_credits: 'Subscription credits allocated', topup_credits: 'Top-up credits allocated', credits_granted: 'Credits granted', renewal: 'Subscription renewed', upgrade: 'Subscription upgraded', downgrade: 'Subscription downgraded' } as Record<string, string>)[item.event || ''];
                      const label = purchaseLabel || (item.type === 'product_activity' && item.event && PRODUCT_LABELS[item.event]) || (item.operationType && OPERATION_LABELS[item.operationType]) || cfg.label;
                      const name = item.extensionName || (item.type === 'image_generation' && !item.operationType ? item.mode : undefined);
                      const credit = item.creditStatus && CREDIT_STATUS[item.creditStatus];
                      return (
                        <div key={`${item.type}:${item._id}`} className="um-activity-item">
                          <div className="um-activity-icon" style={{ color: cfg.color }}>{cfg.icon}</div>
                          <div className="um-activity-body">
                            <div className="um-activity-title">
                              <span className="um-activity-label">{label}</span>
                              {name && <span className="um-activity-name">· {name}</span>}
                            </div>
                            <div className="um-activity-meta">
                              {(item.type === 'app_launch' || item.type === 'login') && (
                                <>
                                  {item.type === 'login' && item.loginMethod && <span>via {item.loginMethod}</span>}
                                  {item.platform && <span className={`um-store ${item.platform}`}>{item.platform === 'ios' ? 'iOS' : 'Android'}</span>}
                                  {item.osVersion && <span>{item.osVersion}</span>}
                                  {item.appVersion && <span>v{item.appVersion}{item.buildNumber ? ` (${item.buildNumber})` : ''}</span>}
                                  {item.deviceModel && <span>{item.deviceModel}</span>}
                                </>
                              )}
                              {item.purchaseStatus && <>
                                {item.source && <span className="um-source-tag">{CREDIT_SOURCE_LABELS[item.source] || 'Unknown source'}</span>}
                                <span className={`um-status-tag ${item.purchaseStatus}`}>{item.purchaseStatus}</span>
                                <span>{item.type !== 'topup' && item.planId ? PLAN_LABELS[item.planId] || item.planId : item.productId}</span>
                                {item.credits != null && <span>{item.credits} credits</span>}
                                {item.priceAmount != null && <span>{item.priceCurrency} {item.priceAmount.toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>}
                                {item.platform && <span>{item.platform}</span>}
                                {item.environment === 'sandbox' && <span className="um-source-tag sandbox">{SANDBOX_LABEL}</span>}
                                {item.adminName && <span>by {item.adminName}</span>}
                                {item.remark && <span className="um-remark-text">{item.remark}</span>}
                              </>}
                              {item.type === 'app_session' && <><span>{formatDuration(item.durationMs)} foreground</span><span>{item.sessionEnded && !item.sessionRecovered ? 'Completed' : 'Last recorded time'}</span></>}
                              {item.type === 'product_activity' && <><span>{item.source}</span>{item.imageCount != null && <span>{item.imageCount} images</span>}{item.result && <span>{item.result}</span>}</>}
                              {item.type === 'image_generation' && (
                                <>
                                  {credit ? (
                                    <span className={`um-status-tag ${credit.tag}`}>{item.credits} credits {credit.label}</span>
                                  ) : (
                                    <span>No credit record</span>
                                  )}
                                  {item.imageState === 'deleted' && (
                                    <span className="um-status-tag cancelled" title="The user deleted this image from their gallery. The credit record is unaffected.">Deleted by user</span>
                                  )}
                                  {item.imageState === 'unlinked' && (
                                    <span title="This refunded attempt is not linked to an image: it was retried, deleted, or failed before the image record was created.">No linked image</span>
                                  )}
                                  {item.retried && <span title="The same image was retried under a later credit record.">Retried later</span>}
                                  {item.status && <span className={`um-status-tag ${item.status}`}>{item.status}</span>}
                                  {item.aiModel && <span>{item.aiModel}</span>}
                                  {/* Sent only for attempts that were not charged, so a refunded retry shows why. */}
                                  {item.errorMessage && <span className="um-activity-error">{item.errorMessage}</span>}
                                </>
                              )}
                              {item.type === 'extension_creation' && (
                                <>
                                  <span>{item.creditCost ?? 0} credits / use</span>
                                  <span className={`um-status-tag ${item.isPublished ? 'completed' : 'pending'}`}>{item.isPublished ? 'Published' : 'Draft'}</span>
                                  {item.reportStatus && item.reportStatus !== 'Active' && <span className="um-status-tag cancelled">{item.reportStatus}</span>}
                                </>
                              )}
                            </div>
                          </div>
                          <div className="um-activity-time">{formatDate(item.createdAt)}</div>
                        </div>
                      );
                    })}
                  </div>
                )}
                <LazyListFooter rootRef={activityScrollRef} count={activityLogs.length} noun="activities"
                  loading={activityFeed.loading} error={activityFeed.error} hasMore={activityFeed.hasMore}
                  onLoadMore={activityFeed.loadMore} onRetry={activityFeed.retry} />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Relationship Sub-Modal */}
      {relationshipModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: '16px' }} onClick={(e) => { e.stopPropagation(); setRelationshipModal(null); }}>
          <div style={{ backgroundColor: 'var(--color-surface)', borderRadius: '12px', width: '100%', maxWidth: '420px', maxHeight: '70vh', display: 'flex', flexDirection: 'column', border: '1px solid var(--color-border)' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid var(--color-border)' }}>
              <h3 style={{ margin: 0, fontSize: '16px', color: 'var(--color-text)' }}>{relationshipModal.title}</h3>
              <button onClick={() => setRelationshipModal(null)} style={{ background: 'none', border: 'none', color: 'var(--color-muted)', cursor: 'pointer', fontSize: '18px' }}><FiX /></button>
            </div>
            <div style={{ overflowY: 'auto', padding: '12px 20px', flex: 1 }}>
              {relationshipLoading ? (
                <div style={{ textAlign: 'center', padding: '24px', color: 'var(--color-muted)' }}>Loading...</div>
              ) : relationshipItems.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '24px', color: 'var(--color-muted)' }}>No items found.</div>
              ) : (relationshipModal.type === 'followers' || relationshipModal.type === 'following') ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
                  {relationshipItems.map((u: any) => (
                    <div key={u.userId} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', padding: '14px 8px', borderRadius: '8px', backgroundColor: 'var(--color-surface-hover)', border: '1px solid var(--color-border)', textAlign: 'center' }}>
                      {u.profilePicture ? (
                        <img src={u.profilePicture} alt="" style={{ width: '48px', height: '48px', borderRadius: '50%', objectFit: 'cover' }} />
                      ) : (
                        <div style={{ width: '48px', height: '48px', borderRadius: '50%', backgroundColor: 'var(--color-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-muted)', fontSize: '18px', fontWeight: 600 }}>
                          {(u.name || '?')[0].toUpperCase()}
                        </div>
                      )}
                      <div style={{ minWidth: 0, width: '100%' }}>
                        <div style={{ fontSize: '13px', fontWeight: 500, color: 'var(--color-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.name}</div>
                        <div style={{ fontSize: '11px', color: 'var(--color-muted)', marginTop: '2px' }}>{PLAN_LABELS[u.plan ?? ''] ?? 'Free'}{u.isCreator ? ' · Creator' : ''}</div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
                  {relationshipItems.map((ext: any) => (
                    <div key={ext._id} style={{ borderRadius: '8px', overflow: 'hidden', backgroundColor: 'var(--color-surface-hover)', border: '1px solid var(--color-border)' }}>
                      {ext.thumbnailImageUrl ? (
                        <img src={ext.thumbnailImageUrl} alt={ext.Name} style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', display: 'block' }} />
                      ) : (
                        <div style={{ width: '100%', aspectRatio: '1', backgroundColor: 'var(--color-surface-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-border-strong)' }}>
                          <FiCamera size={20} />
                        </div>
                      )}
                      <div style={{ padding: '6px 8px' }}>
                        <div style={{ fontSize: '12px', fontWeight: 500, color: 'var(--color-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{ext.Name}</div>
                        <div style={{ fontSize: '11px', color: 'var(--color-muted)', display: 'flex', alignItems: 'center', gap: '3px', marginTop: '2px' }}><FiHeart size={10} /> {ext.likeCount}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
